import { useEffect, useState } from 'react';
import { useTranslation } from '../../i18n/LocaleContext';
import { Link } from 'react-router-dom';
import { memberApi } from '@formosa/shared/api/member';
import { ApiError } from '@formosa/shared/api/request';
import { DEFAULT_REGISTER_CONFIG } from '@formosa/shared/utils/registerConfig';
import AuthPhoneCountryFields from '../../components/auth/AuthPhoneCountryFields';

const MODES = {
  EMAIL: 'email',
  PHONE: 'phone',
};

export default function ForgotPasswordPage() {
  const { t } = useTranslation();
  const [mode, setMode] = useState(MODES.EMAIL);
  const [registerConfig, setRegisterConfig] = useState(DEFAULT_REGISTER_CONFIG);
  const [loading, setLoading] = useState(false);
  const [sendingCode, setSendingCode] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [form, setForm] = useState({
    email: '',
    phoneNumber: '',
    countryEnName: '',
    countryKey: '',
    phoneAreaCode: '',
    verifyCode: '',
    newPassword: '',
  });

  useEffect(() => {
    let cancelled = false;
    memberApi
      .getRegisterConfig()
      .then((config) => {
        if (!cancelled) {
          setRegisterConfig(config);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  function updateField(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
  }

  function updatePhoneCountry(patch) {
    setForm((prev) => ({
      ...prev,
      countryKey: patch.countryKey ?? prev.countryKey,
      countryEnName: patch.countryEnName ?? prev.countryEnName,
      phoneAreaCode: patch.phoneAreaCode ?? prev.phoneAreaCode,
    }));
  }

  async function handleSendVerifyCode() {
    setError('');
    setSuccess('');
    setSendingCode(true);

    try {
      if (mode === MODES.EMAIL) {
        if (!form.email) {
          throw new ApiError('請先填寫 Email');
        }
        await memberApi.sendVerifyCodeEmail({
          scene: 'RESET_LOGIN_PASSWORD',
          email: form.email,
        });
      } else {
        if (!form.phoneNumber) {
          throw new ApiError('請先填寫手機號碼');
        }
        await memberApi.sendVerifyCodePhone({
          scene: 'RESET_LOGIN_PASSWORD',
          countryEnName: form.countryEnName,
          phoneAreaCode: form.phoneAreaCode,
          phoneNumber: form.phoneNumber,
        });
      }
      setSuccess(mode === MODES.EMAIL ? t('emailVerification.accepted') : '驗證碼已發送');
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : '驗證碼發送失敗';
      setError(message);
    } finally {
      setSendingCode(false);
    }
  }

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await memberApi.forgetPassword({
        resetType: 'LOGIN',
        newPassword: form.newPassword,
        verifyCode: form.verifyCode,
        email: mode === MODES.EMAIL ? form.email : undefined,
        phoneNumber: mode === MODES.PHONE ? form.phoneNumber : undefined,
        countryEnName: mode === MODES.PHONE ? form.countryEnName : undefined,
      });
      setSuccess('密碼已重設，請使用新密碼登入');
    } catch (err) {
      const message =
        err instanceof ApiError ? err.message : '重設密碼失敗，請稍後再試';
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-card">
      <h1>忘記密碼</h1>
      <p>透過 Email 或手機驗證碼重設登入密碼</p>

      <div className="tabs">
        <button
          type="button"
          className={`tab ${mode === MODES.EMAIL ? 'active' : ''}`}
          onClick={() => setMode(MODES.EMAIL)}
        >
          Email
        </button>
        <button
          type="button"
          className={`tab ${mode === MODES.PHONE ? 'active' : ''}`}
          onClick={() => setMode(MODES.PHONE)}
        >
          手機
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <form onSubmit={handleSubmit}>
        {mode === MODES.EMAIL ? (
          <div className="form-field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => updateField('email', e.target.value)}
              required
            />
          </div>
        ) : (
          <>
            <AuthPhoneCountryFields
              variant="form"
              idPrefix="forgot-phone"
              countryKey={form.countryKey}
              countryEnName={form.countryEnName}
              phoneAreaCode={form.phoneAreaCode}
              onChange={updatePhoneCountry}
              allowedEnNames={registerConfig.enNames}
              disabled={loading || sendingCode}
            />
            <div className="form-field">
              <label htmlFor="phoneNumber">手機號碼</label>
              <input
                id="phoneNumber"
                value={form.phoneNumber}
                onChange={(e) => updateField('phoneNumber', e.target.value)}
                required
              />
            </div>
          </>
        )}

        <div className="form-field">
          <label htmlFor="verifyCode">驗證碼</label>
          <div className="form-row">
            <input
              id="verifyCode"
              value={form.verifyCode}
              onChange={(e) => updateField('verifyCode', e.target.value)}
            />
            <button
              type="button"
              className="btn btn-secondary"
              onClick={handleSendVerifyCode}
              disabled={sendingCode}
              style={{ flexShrink: 0 }}
            >
              {sendingCode ? '發送中…' : '發送驗證碼'}
            </button>
          </div>
        </div>

        <div className="form-field">
          <label htmlFor="newPassword">新密碼（6～12 字，不含空白）</label>
          <input
            id="newPassword"
            type="password"
            value={form.newPassword}
            onChange={(e) => updateField('newPassword', e.target.value)}
            required
            minLength={6}
            maxLength={12}
          />
        </div>

        <button
          type="submit"
          className="btn btn-primary btn-block"
          disabled={loading}
        >
          {loading ? '處理中…' : '重設密碼'}
        </button>
      </form>

      <div className="auth-footer">
        <Link to="/login">返回登入</Link>
      </div>
    </div>
  );
}
